// EVOLVE Technologies — isolated demo content. No real data touched.

export const CEO_PORTRAIT =
    "https://static.prod-images.emergentagent.com/jobs/8323456d-e952-4389-bd02-2fbbefce2d43/images/515b32ef616f801a9193505165aaa9adb368c74d452e0004b37a24f0c8937ec3.png";

export const EVOLVE = {
    name: "EVOLVE Technologies",
    industry: "AI + Enterprise SaaS",
    employees: 248,
    departments: 10,
    countries: 8,
    revenue: "₹4.2 Cr",
    cash: "₹18 Cr",
    burn: "₹62 L",
    runway: "29 months",
    approvals: 14,
    risks: 7,
    projects: 26,
    customers: 143,
    enterprise: 39,
    vendors: 52,
    reports: 650,
};

export const READY_STATS = [
    ["248", "Employees"],
    ["10", "Departments"],
    ["650", "Reports"],
    ["24", "Decision Cards"],
    ["52", "Vendors"],
    ["39", "Enterprise Clients"],
];

const c = (o) => ({ card_type: "ai_recommendation", priority: "medium", confidence: 90, metrics: [], ...o });

// The 6 suggested prompts, each with a fully pre-scripted rich response (no LLM cost).
export const SCRIPTED = [
    {
        id: "hire",
        prompt: "Should we hire 3 engineers?",
        answer:
            "**Verdict: Yes — but stagger it.** Engineering hiring urgency is real (product velocity + 5 open roles), and your **29-month runway** comfortably absorbs it. Onboard 2 now and the 3rd in 30 days to smooth the burn step-up.",
        confidence_score: 91,
        confidence_label: "Very high",
        department_signals: [
            { department: "Finance", signal: "Runway 29 months — healthy headroom", status: "good" },
            { department: "HR", signal: "Engineering urgency, 5 open roles", status: "watch" },
            { department: "Operations", signal: "Capacity headroom for 2 large projects", status: "good" },
        ],
        risks: ["Monthly burn rises ~10% (+₹6.15L) — offset with marketing efficiency."],
        money_impact: {
            cost: "+₹6.15L / month (3 engineers, loaded)",
            cash_impact: "-₹6.15L / month",
            runway_impact: "29 → 27.5 months (est)",
            expected_profit: "Faster roadmap → revenue impact in ~2 quarters",
            is_estimate: true,
        },
        decision_cards: [
            c({
                card_type: "hiring_decision", priority: "high", department: "HR", confidence: 91,
                title: "Approve 3 Engineering Hires (staggered)",
                summary: "Approve the 3 requisitions; onboard 2 immediately and 1 in 30 days to protect cash.",
                action: "Approve reqs · onboard 2 now, 1 in 30 days",
                expected_impact: "Faster product velocity, unblock 2 at-risk projects",
                risk: "Burn +10%; mitigate via marketing reallocation",
                metrics: [
                    { label: "Monthly Cost", value: "+₹6.15L" },
                    { label: "Runway", value: "29 → 27.5 mo" },
                    { label: "Capacity", value: "+18%" },
                ],
            }),
        ],
        follow_up_questions: ["Which roles specifically?", "Impact on Q3 roadmap?", "Can we offset with a marketing trim?"],
        value: {
            title: "You avoided over-hiring",
            message: "Instead of reading Finance, HR and Operations reports separately, CEO GPT combined them and recommended a staggered plan that protects your runway.",
            time_saved: "2.5 Hours", departments: ["Finance", "HR", "Operations"], confidence: 91, impact: "High",
        },
    },
    {
        id: "marketing",
        prompt: "Can I increase marketing budget?",
        answer:
            "**Yes — selectively.** Cash is safe and **LTV:CAC is 8:1** with blended **ROAS 3.1x**. But two campaigns are underperforming at ROAS 1.4x. Add **₹10L to top channels and pause the two weak campaigns** rather than a flat increase.",
        confidence_score: 88,
        confidence_label: "High",
        department_signals: [
            { department: "Marketing", signal: "Blended ROAS 3.1x, LTV:CAC 8:1", status: "good" },
            { department: "Finance", signal: "Cash ₹18 Cr — increase is safe", status: "good" },
            { department: "Sales", signal: "Pipeline +18% MoM, can absorb leads", status: "good" },
        ],
        risks: ["2 campaigns at ROAS 1.4x are dragging blended efficiency."],
        money_impact: {
            cost: "+₹10L / month (reallocated)",
            expected_revenue: "+₹31L pipeline (at 3.1x ROAS, est)",
            expected_profit: "+₹22L gross (est)",
            is_estimate: true,
        },
        decision_cards: [
            c({
                card_type: "budget_recommendation", priority: "high", department: "Marketing", confidence: 88,
                title: "Reallocate +₹10L to top channels · pause 2 weak campaigns",
                summary: "Shift spend from ROAS-1.4x campaigns into proven channels to raise blended efficiency.",
                action: "Approve reallocation, pause 2 campaigns",
                expected_impact: "+₹31L incremental pipeline this quarter",
                risk: "Attribution lag ~2 weeks",
                metrics: [
                    { label: "Blended ROAS", value: "3.1x" },
                    { label: "LTV:CAC", value: "8:1" },
                    { label: "Extra Pipeline", value: "+₹31L" },
                ],
            }),
        ],
        follow_up_questions: ["Which channels perform best?", "What is the payback period?", "Show the 2 weak campaigns"],
        value: {
            title: "You optimized campaign ROI",
            message: "CEO GPT checked Finance, Marketing and Sales together and turned a blunt 'increase budget' into a precise reallocation.",
            time_saved: "2 Hours", departments: ["Finance", "Marketing", "Sales"], confidence: 88, impact: "High",
        },
    },
    {
        id: "summary",
        prompt: "Summarize today's business.",
        answer:
            "**Today at EVOLVE:** Revenue **₹4.2 Cr/mo (+8.4%)**, runway **29 months**, weighted pipeline **₹4.9 Cr** (92% of target). **14 approvals** pending and **7 open risks**. Top 3 priorities: (1) 2 overdue receivables ₹68L, (2) 6 churn-risk accounts ₹84L ARR, (3) 2 enterprise MSAs expiring worth ₹1.2 Cr.",
        confidence_score: 93,
        confidence_label: "Very high",
        department_signals: [
            { department: "Finance", signal: "Revenue +8.4%, runway 29mo", status: "good" },
            { department: "Sales", signal: "Forecast at 92% of target", status: "watch" },
            { department: "Customer Support", signal: "6 churn-risk accounts ₹84L ARR", status: "risk" },
            { department: "Legal", signal: "2 MSAs expiring ₹1.2 Cr", status: "watch" },
        ],
        risks: ["₹68L overdue receivables", "₹84L ARR churn risk", "₹1.2 Cr MSAs expiring in 45 days"],
        money_impact: {},
        decision_cards: [
            c({
                card_type: "kpi_summary", priority: "medium", department: "Strategy", confidence: 93,
                title: "Today's Executive Brief",
                summary: "One-glance snapshot of company health with the three items that need your attention.",
                action: "Review 3 flagged priorities",
                expected_impact: "Protect ₹2.5 Cr+ of at-risk value",
                metrics: [
                    { label: "Revenue", value: "₹4.2 Cr" },
                    { label: "Runway", value: "29 mo" },
                    { label: "Open Risks", value: "7" },
                ],
            }),
        ],
        follow_up_questions: ["Show my biggest risks", "Which department needs attention?", "What should I approve today?"],
        value: {
            title: "10 departments, one brief",
            message: "CEO GPT read across all 10 departments and 650 reports to give you a single morning brief — no dashboards opened.",
            time_saved: "3 Hours", departments: ["Finance", "Sales", "Customer Support", "Legal"], confidence: 93, impact: "High",
        },
    },
    {
        id: "risks",
        prompt: "Show my biggest risks.",
        answer:
            "**Your 7 risks, ranked by money at stake:**\n1. **Churn** — 6 accounts, **₹84L ARR** (onboarding + integration bug).\n2. **Contract expiry** — 2 enterprise MSAs, **₹1.2 Cr ARR** in 45 days.\n3. **Overdue receivables** — **₹68L** (2 invoices >45d).\n4. **Compliance** — GDPR DPA gap (medium).\n5. Competitor price war (-20%).",
        confidence_score: 90,
        confidence_label: "Very high",
        department_signals: [
            { department: "Customer Support", signal: "₹84L ARR churn risk", status: "risk" },
            { department: "Legal", signal: "₹1.2 Cr MSAs expiring + GDPR gap", status: "risk" },
            { department: "Finance", signal: "₹68L overdue receivables", status: "watch" },
        ],
        risks: ["Churn ₹84L ARR", "MSA expiry ₹1.2 Cr", "Overdue receivables ₹68L", "GDPR DPA gap", "Competitor price war"],
        money_impact: { risk: "₹2.5 Cr+ of ARR and cash at stake if unaddressed", is_estimate: true },
        decision_cards: [
            c({
                card_type: "risk_alert", priority: "critical", department: "Customer Support", confidence: 90,
                title: "Address Top 3 Risks This Week",
                summary: "Churn save-play on 6 accounts, renew 2 MSAs early, collect overdue receivables.",
                action: "Assign owners + escalate churn accounts",
                expected_impact: "Protect ₹2.5 Cr+ ARR & cash",
                risk: "Delay compounds churn probability",
                metrics: [
                    { label: "ARR at Risk", value: "₹84L" },
                    { label: "Contracts", value: "₹1.2 Cr" },
                    { label: "Overdue", value: "₹68L" },
                ],
            }),
        ],
        follow_up_questions: ["Which accounts are churning?", "Draft a renewal plan for the MSAs", "Who owns receivables collection?"],
        value: {
            title: "You surfaced a hidden cash-flow risk",
            message: "CEO GPT scanned every department, quantified each risk in ₹, and ranked them so you act on what matters most.",
            time_saved: "2.5 Hours", departments: ["Customer Support", "Legal", "Finance"], confidence: 90, impact: "High",
        },
    },
    {
        id: "attention",
        prompt: "Which department needs attention?",
        answer:
            "**Customer Support.** It's carrying the most at-risk value: **6 churn-risk accounts (₹84L ARR)**, **312 open tickets**, rising attrition, and a recurring integration bug. CSAT is holding at 4.3 but trending down. This is your highest-leverage intervention this week.",
        confidence_score: 89,
        confidence_label: "High",
        department_signals: [
            { department: "Customer Support", signal: "₹84L ARR churn, 312 open tickets", status: "risk" },
            { department: "HR", signal: "Support attrition rising", status: "watch" },
            { department: "Operations", signal: "Integration bug recurring", status: "watch" },
        ],
        risks: ["Churn ₹84L ARR", "Support attrition", "Recurring integration bug inflating tickets"],
        money_impact: { risk: "₹84L ARR exposed", cost: "Save-play ~₹4L", expected_profit: "Retain ₹84L ARR", is_estimate: true },
        decision_cards: [
            c({
                card_type: "customer_alert", priority: "high", department: "Customer Support", confidence: 89,
                title: "Stabilize Customer Support",
                summary: "Launch a churn save-play, fast-track the integration bug fix, and add 1 CS hire.",
                action: "Approve save-play + engineering hotfix",
                expected_impact: "Retain ₹84L ARR, cut ticket backlog 30%",
                risk: "Attrition may worsen without action",
                metrics: [
                    { label: "Churn ARR", value: "₹84L" },
                    { label: "Open Tickets", value: "312" },
                    { label: "CSAT", value: "4.3/5" },
                ],
            }),
        ],
        follow_up_questions: ["Draft the churn save-play", "How fast can we ship the bug fix?", "Should we hire a CS lead?"],
        value: {
            title: "You detected customer churn risk",
            message: "CEO GPT compared all 10 departments and pinpointed exactly where your attention returns the most value.",
            time_saved: "2 Hours", departments: ["Customer Support", "HR", "Operations"], confidence: 89, impact: "High",
        },
    },
    {
        id: "expand",
        prompt: "Can we expand internationally?",
        answer:
            "**Yes — but stage-gate it.** UAE + Singapore show strong enterprise demand and a competitor gap, and your **₹18 Cr cash / 29-month runway** supports it. Start with a lightweight GTM validation, and close the **GDPR/SOC 2** gaps first. **Review Required** before full commit.",
        confidence_score: 86,
        confidence_label: "High",
        department_signals: [
            { department: "Strategy", signal: "Strong demand + competitor gap (UAE/SG)", status: "good" },
            { department: "Finance", signal: "₹18 Cr cash, 29-mo runway", status: "good" },
            { department: "Legal", signal: "GDPR DPA + SOC 2 must close first", status: "watch" },
            { department: "HR", signal: "Needs 3-4 local hires", status: "watch" },
        ],
        risks: ["Compliance gaps could delay enterprise deals", "Local hiring + entity setup cost"],
        money_impact: {
            cost: "₹45-60L (stage-1 validation, est)",
            expected_revenue: "₹3-5 Cr ARR potential in 12-18 mo (est)",
            runway_impact: "Minimal in stage 1",
            is_estimate: true,
        },
        decision_cards: [
            c({
                card_type: "approval", priority: "high", department: "Strategy", confidence: 86,
                title: "Approve UAE + Singapore GTM Validation (Stage 1)",
                summary: "Fund a lightweight market validation; close compliance gaps in parallel before full entry.",
                action: "Approve stage-1 budget · Review before full commit",
                expected_impact: "Unlock ₹3-5 Cr ARR pipeline (est)",
                risk: "Compliance gaps must close first",
                metrics: [
                    { label: "Stage-1 Cost", value: "₹45-60L" },
                    { label: "ARR Potential", value: "₹3-5 Cr" },
                    { label: "Runway", value: "Safe" },
                ],
            }),
            c({
                card_type: "compliance_alert", priority: "medium", department: "Legal", confidence: 84,
                title: "Close GDPR DPA + SOC 2 Before Entry",
                summary: "Two compliance items gate enterprise credibility in the new markets.",
                action: "Assign legal owner, 30-day plan",
                expected_impact: "Removes enterprise deal blockers",
                risk: "Medium compliance exposure",
                metrics: [{ label: "Items", value: "2" }, { label: "SOC 2 Due", value: "60 days" }],
            }),
        ],
        follow_up_questions: ["What's the stage-1 GTM plan?", "Who leads the expansion?", "UAE or Singapore first?"],
        value: {
            title: "You validated a growth direction",
            message: "CEO GPT combined Strategy, Finance, Legal and HR to turn a big question into a safe, staged plan.",
            time_saved: "3 Hours", departments: ["Strategy", "Finance", "Legal", "HR"], confidence: 86, impact: "High",
        },
    },
];

export const HELP_TIPS = [
    "Ask naturally — like talking to your best analyst.",
    "Approve, Simulate or Reject right from Decision Cards.",
    "Open the Executive Dashboard anytime.",
    "Every answer combines company-wide data across 10 departments.",
];

// Attach Money Meter movement to each scripted decision.
const MM_MAP = {
    hire: { current: 63, label: "Healthy", card: { before: 63, after: 61, reason: "Burn rises ~10% from new hires; staggering protects runway" } },
    marketing: { current: 63, label: "Healthy", card: { before: 63, after: 66, reason: "ROI-positive reallocation lifts spend efficiency" } },
    summary: { current: 63, label: "Healthy" },
    risks: { current: 63, label: "Healthy", card: { before: 63, after: 68, reason: "Resolving the top 3 risks protects ₹2.5Cr+ ARR & cash" } },
    attention: { current: 63, label: "Healthy", card: { before: 63, after: 67, reason: "Churn save-play retains ₹84L ARR" } },
    expand: { current: 63, label: "Healthy", card: { before: 63, after: 61, reason: "Stage-1 spend now; upside realized over 12-18 months" } },
};
SCRIPTED.forEach((s) => {
    const mm = MM_MAP[s.id];
    if (!mm) return;
    s.money_meter = { current: mm.current, label: mm.label };
    if (mm.card && s.decision_cards && s.decision_cards[0]) s.decision_cards[0].money_meter = mm.card;
});

export const COMPLETION = {
    experienced: [
        "24 Executive Decision Frameworks",
        "10 Department Intelligence Agents",
        "Cross-department reasoning",
        "Executive Decision Cards",
        "Business Conversations",
        "AI-powered Recommendations",
    ],
    stats: [
        ["~15 Hours", "Executive time saved"],
        ["10 → 1", "Departments unified"],
        ["3.1s", "Avg decision time"],
        ["24", "Decision frameworks"],
    ],
};
